import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LogOut, ClipboardList, CalendarClock, BookOpen, FolderOpen, MessageSquare, GraduationCap, Clock, User } from "lucide-react";

const CARDS = [
  { icon: CalendarClock, title: "Upcoming Activities", note: "Scheduled volunteer activities will appear here." },
  { icon: BookOpen, title: "Program Information", note: "Program briefs shared with you will appear here." },
  { icon: FolderOpen, title: "Approved Files", note: "Files shared with volunteers will appear here." },
  { icon: MessageSquare, title: "Messages", note: "Messages from the coordination team will appear here." },
  { icon: GraduationCap, title: "Training", note: "Training and onboarding materials will appear here." },
  { icon: Clock, title: "Volunteer Hours", note: "Logged hours will appear here once enabled." },
];

const STATUS_LABEL = { assigned: "Assigned", in_progress: "In progress", completed: "Completed" };
const STATUS_CLS = { assigned: "bg-amber-100 text-amber-700", in_progress: "bg-blue-100 text-blue-700", completed: "bg-green-100 text-green-700" };

export default function VolunteerWorkspace() {
  const { user, logout } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get("/volunteer/assignments").then(({ data }) => setAssignments(data)).catch(() => {}); }, []);
  const doLogout = async () => { await logout(); nav("/"); };
  return (
    <div className="min-h-screen bg-[#FAFAFA] font-admin" data-testid="volunteer-workspace">
      <header className="bg-[#1F4D2E] text-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><User size={18} /><span className="font-bold">Volunteer Workspace</span></div>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden sm:inline text-white/80" data-testid="vw-email">{user?.email}</span>
            <Link to="/" target="_blank" className="text-white/80 hover:text-white">View site</Link>
            <button onClick={doLogout} data-testid="vw-logout" className="inline-flex items-center gap-1 hover:text-[#E0A526]"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-12">
        <h1 className="font-display text-3xl font-bold text-[#1F4D2E] mb-1">Welcome, {user?.name || "volunteer"}.</h1>
        <p className="text-sm text-[#4F6B54] mb-8">Thank you for volunteering with Refugium Ethiopia. Your approved tools appear below.</p>

        <div className="bg-white border border-[#1F4D2E]/10 rounded-xl p-5 mb-6" data-testid="vw-assignments">
          <h2 className="font-semibold text-[#1F4D2E] flex items-center gap-2 mb-3"><ClipboardList size={18} className="text-[#C1272D]" /> My Assignments</h2>
          {assignments.length === 0 ? (
            <p className="text-sm text-[#4F6B54]" data-testid="vw-assignments-empty">You have no assignments yet. Your coordinator will add tasks here.</p>
          ) : (
            <div className="space-y-3">
              {assignments.map((a) => (
                <div key={a.id} className="border border-gray-100 rounded-lg p-4" data-testid={`vw-assignment-${a.id}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <p className="font-semibold text-[#1F4D2E]">{a.title}</p>
                      {a.details && <p className="text-sm text-[#4F6B54] mt-1">{a.details}</p>}
                      {a.due_date && <p className="text-xs text-[#4F6B54] mt-1">Due: {a.due_date}</p>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${STATUS_CLS[a.status] || "bg-gray-100"}`}>{STATUS_LABEL[a.status] || a.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CARDS.map((c) => (
            <div key={c.title} className="bg-white border border-[#1F4D2E]/10 rounded-xl p-5" data-testid={`vw-card-${c.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <c.icon className="text-[#C1272D] mb-3" size={22} />
              <h3 className="font-semibold text-[#1F4D2E]">{c.title}</h3>
              <p className="text-sm text-[#4F6B54] mt-1">{c.note}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
